import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { OWDataType } from "@multiweb/types/contracts";
import { CultureInfo } from "../../Dom/CultureInfo";
import { BigNumberProxy } from "../DataTypes/BigNumber";
import { IBigNumber } from "../DataTypes/IBigNumber";
import { FormatProviderRegistrator } from "../FormatProviderRegistrator/FormatProviderRegistrator";
import { GlobalContext } from "../GlobalContext";

const logger = LazyLoadingSectionLogger.create(LoggingSection.core);

export type GetFormattedNumberStringParams = {
    displayFormat?: string;
    currency?: string;
    dataType?: OWDataType | string;
};

export class NumberUtils {
    public static readonly BYTE_MAX = 255;
    public static readonly BYTE_MIN = 0;

    public static readonly SHORT_MAX = 32767;
    public static readonly SHORT_MIN = -32768;

    public static readonly INT_MAX = 2147483647;
    public static readonly INT_MIN = -2147483648;

    public static readonly INT64_MAX = 4503599627370496;
    public static readonly INT64_MIN = -4503599627370496;

    public static readonly SINGLE_MAX = 1.7e+38;
    public static readonly SINGLE_MIN = -1.7e+38;

    public static readonly DOUBLE_MAX = 3.4e+38;
    public static readonly DOUBLE_MIN = -3.4e+38;

    public static readonly DECIMAL_MAX = 7.9e+28;
    public static readonly DECIMAL_MIN = -7.9e+28;

    /**
     * Formátovací masky: https://wiki-dokumentace/index.php?title=Form%C3%A1tovac%C3%AD_masky
     */

    /**oddělovač tisíců a 2 desetinná místa, to je obvyklé pro peněžní částky.*/
    public static readonly DEFAULT_FORMAT_MONEY = "#,##0.00";
    /**oddělovat tisíců a bez desetinných míst.*/
    public static readonly DEFAULT_FORMAT_REAL_NUMBER = "#,##0.###############";
    /**oddělovat tisíců a bez desetinných míst.*/
    public static readonly DEFAULT_FORMAT_WHOLE_NUMBER = "#,##0";
    /**general - oddělovač tisíců a variabilní desetinná místa.*/
    public static readonly DEFAULT_FORMAT_GENERAL = "g";

    /**
     * Podporované datové typy https://wiki-dokumentace/index.php?title=Podporovan%C3%A9_datov%C3%A9_typy
     */

    public static readonly DATE_TYPES = new Set<OWDataType | string>([
        OWDataType.date,
        OWDataType.time,
        OWDataType.dateTime,
    ]);

    public static readonly NUMBER_TYPES = new Set<OWDataType | string>([
        OWDataType.decimal,
        OWDataType.money,
        OWDataType.bcd,
        OWDataType.double,
        OWDataType.single,
        OWDataType.byte,
        OWDataType.short,
        OWDataType.integer,
        OWDataType.int64,
    ]);

    private static readonly _MONEY_NUMBER_TYPES = new Set<OWDataType | string>([
        OWDataType.decimal,
        OWDataType.money,
    ]);

    private static readonly _REAL_NUMBER_TYPES = new Set<OWDataType | string>([
        OWDataType.bcd,
        OWDataType.double,
        OWDataType.single,
    ]);

    private static readonly _WHOLE_NUMBER_TYPES = new Set<OWDataType | string>([
        OWDataType.byte,
        OWDataType.short,
        OWDataType.integer,
        OWDataType.int64,
    ]);

    /**
     * Regularni vyraz ktery zjisti, jestli cislo je ve formatu 123.45 nebo 12.3e-45
     * a rozdeli ho na znamenko, desetinnou cast, celou cast a exponent.
     * @private
     */
    private static readonly _NEX = /^([+-])?(\d*)(?:\.(\d+))?(?:e([+-])(\d+))?$/;
    private static readonly _HI = ["⁰", "¹", "²", "³", "⁴", "⁵", "⁶", "⁷", "⁸", "⁹"]; // horni indexy ⁻ ⁺
    private static readonly _FEX = /([0#,]+)(?:\.([0#]+))?/;
    /** Regulární výraz který zjistí, zda je znak oddělovače tisíců mezera (včetně nedělitelné mezery). */
    public static readonly WHITESPACE_GROUP_SEPARATOR_REGEX = /^(\s)$/;
    /** Regulární výraz pro nahrazení mezer v řetězci. */
    public static readonly WHITESPACE_REGEX_GLOBAL = /\s/g;

    private static readonly _NUM_TO_STR_FUNCS: any = {};

    public static displayFormatToNumberFormat(format: string): INumberFormatOptions {
        const s = NumberUtils._FEX.exec(format);
        const r: INumberFormatOptions = {};
        if (s) {
            const d = s[2];
            if (d != null)
                r.maximumFractionDigits = (d.lastIndexOf("0") + 1) || d.length; // .## , .#0 bere jako .00 - casem doladit
        }
        return r;
    }

    public static numberToString(numStr: string, params: Readonly<INumberFormatOptions>): string {
        const n = numStr,
            nf = CultureInfo.getCultureInfo().numberFormat,
            s = NumberUtils._NEX.exec(n);

        if (!s)
            return n.replace(/Infinity/gi, "∞");

        const sign = (s[1] === undefined || s[1] === "+") ? "" : "-";
        let fractions = s[3] ?? ""; // desetinna cast cisla
        let integer = s[2]; // cela cast cisla

        if (s[4]) { // jestli bylo nalezeno znamenko exponentu
            const hI = NumberUtils._HI;
            const exponentSign = s[4] === "-" ? "⁻" : "";
            const exponent = s[5].replace(/./g, (v) => hI[parseInt(v)]);
            return sign + integer + nf.decimalSeparator + fractions + "×10" + exponentSign + exponent;
        }

        let carry: boolean = false; // příznak, zda se má navýšit celá část čísla po zaokrouhlení

        const minFractionDigits = params.minimumFractionDigits ?? -1;
        const maxFractionDigits = params.maximumFractionDigits ?? -1;

        const minIntegerDigits = params.minimumIntegerDigits;
        if (minFractionDigits > -1 || maxFractionDigits > -1) {
            if (fractions === undefined)
                fractions = "";

            // rozšíření o nuly
            let fractionDigits = fractions.length;
            if (fractionDigits < minFractionDigits)
                fractions += new Array(minFractionDigits - fractionDigits + 1).join("0");

            // zkrácení na maximální délku a zaokrouhlení
            fractionDigits = fractions.length;

            if (fractionDigits !== 0 && fractionDigits > maxFractionDigits) {
                const exceedingDigit: number = +fractions[maxFractionDigits];
                const shouldRound: boolean = exceedingDigit >= 5;
                const trimmedNumberString: string = fractions.substring(0, maxFractionDigits);

                if (trimmedNumberString === "") {
                    // Počet desetinných čísel je 0, nastavíme prázdný řetězec
                    fractions = trimmedNumberString;
                    if (shouldRound) { // Zaokrouhlení na místě desetin později připíšeme do celé části čísla
                        carry = true;
                    }
                } else {
                    if (shouldRound) { // zaokrouhlení nahoru
                        let roundedResult = Number.isSafeInteger(+trimmedNumberString)
                            ? (+trimmedNumberString + 1).toString()
                            : new BigNumberProxy(trimmedNumberString).plus(1).toString();

                        // Doplní nuly před zaokrouhlenou hodnotou - nutné pro případy kdy `+trimmedNumberString === 0`
                        roundedResult = roundedResult.padStart(maxFractionDigits, "0");

                        if (roundedResult.length > maxFractionDigits) {
                            // po zaokrouhlení má desetinná část více číslic než dříve - tzn. vznikl přenos do celé části
                            carry = true;
                            fractions = "0".repeat(maxFractionDigits);
                        } else {
                            fractions = roundedResult;
                        }
                    } else { // zaokrouhlení dolů
                        fractions = trimmedNumberString;
                    }
                }
            }
        }

        if (minIntegerDigits !== undefined && minIntegerDigits > -1) {
            if (integer === undefined)
                integer = "";
            const l = integer.length;
            if (l < minIntegerDigits)
                integer = new Array(minIntegerDigits - l + 1).join("0") + integer;
        }

        // Pokud při zaokrouhlování vznikl přenos do celé části čísla, přičte se 1
        if (carry) {
            integer = Number.isSafeInteger(+integer)
                ? (+integer + 1).toString()
                : new BigNumberProxy(integer).plus(1).toString();
        }

        const grp = params.groupSizes;
        let reg: RegExp;
        if (grp && grp.length === 1) {
            reg = grp[0] === 3 ? /(\d)(?=(\d{3})+\b)/g : new RegExp("(\\d)(?=(\\d{" + grp[0] + "})+\\b)", "g");
            integer = integer.replace(reg, "$1" + nf.groupSeparator);
        }

        if (fractions) { // Jedná se o desetinné číslo
            const decimalPart = nf.decimalSeparator + fractions;
            return sign + integer + decimalPart;
        } else { // Jedná se o celé číslo
            return integer === "0" // Řeší problém se zápornou nulou
                ? integer
                : sign + integer;
        }
    }

    /**
     * Vrací výchozí formátování podle datového typu.
     */
    public static getDefaultFormat(dataType?: OWDataType | string): string {
        if (!dataType)
            return this.DEFAULT_FORMAT_GENERAL;

        if (NumberUtils._MONEY_NUMBER_TYPES.has(dataType))
            return this.DEFAULT_FORMAT_MONEY;
        else if (NumberUtils._REAL_NUMBER_TYPES.has(dataType))
            return this.DEFAULT_FORMAT_REAL_NUMBER;
        else if (NumberUtils._WHOLE_NUMBER_TYPES.has(dataType))
            return this.DEFAULT_FORMAT_WHOLE_NUMBER;

        return this.DEFAULT_FORMAT_GENERAL;
    }

    public static getStandardNumberFormatOptions(format?: string, currency?: string, dataType?: OWDataType | string): INumberFormatOptions {
        if (!format || format === this.DEFAULT_FORMAT_GENERAL) { //pokud je format 'g' nebo prázdný, tak zkontroluje datový typ a případně format upraví
            format = this.getDefaultFormat(dataType);
        }

        const opts: INumberFormatOptions = {};
        // casem doresit Money - nutno pridat k formatu i info o mene pro zobrazeni symbolu

        if (format === "g") { // oddelovac tisicu + promenlivy pocet des. mist
            opts.maximumFractionDigits = 2;
            return opts;
        }

        const grouping = format.indexOf(",") >= 0 || format === "g";

        const frSepPos = format.indexOf(".");
        if (frSepPos >= 0) {
            const splitDisplay = format.split(".");
            const leftD = splitDisplay[0];
            const rightD = splitDisplay[1];

            let minIntD = NumberUtils._getNumOfOccurrences(leftD, "0"); //povinny pocet znaku
            // musi byt v rozsahu 1-21
            if (minIntD <= 0)
                minIntD = 1;
            if (minIntD > 21)
                minIntD = 21;
            let minFractD = NumberUtils._getNumOfOccurrences(rightD, "0"); //povinny pocet znaku
            // musi byt v rozsahu 0-20
            if (minFractD < 0)
                minFractD = 0;
            if (minFractD > 20)
                minFractD = 20;
            let maxFractD = NumberUtils._getNumOfOccurrences(rightD, "#"); //volitelny pocet znaku
            // musi byt v rozsahu 0-20
            if (maxFractD < 0)
                maxFractD = 0;
            if (maxFractD > 20)
                maxFractD = 20;

            opts.minimumIntegerDigits = minIntD;
            opts.minimumFractionDigits = minFractD > 0 ? minFractD + maxFractD : 0;
            opts.maximumFractionDigits = minFractD + maxFractD;
        } else {
            opts.maximumFractionDigits = 0;
        }

        if (grouping) {
            opts.useGrouping = true;
            opts.groupSizes = [3];
        } else
            opts.useGrouping = false;

        if (currency) { // Money
            opts.style = "currency";
            opts.currency = currency;
        }

        return opts;
    }

    /**
     * Naformátuje zadanou hodnotu (v pořadí podle priority):
     * 1. Podle vlastnosti `displayFormat` - řešeno v {@link getNumberToStringFunction}
     * 2. Podle datového typu `dataType` - řešeno v {@link getNumberToStringFunction}
     * 3. Podle nastavené kultury prohlížeče - řešeno v {@link BigNumberProxy.toLocaleString} pro BigNumber
     * a pro obyčejné čísla nativně pomocí {@link Number.toLocaleString}.
     *
     * - Podporuje nastavení znaku měny (např. "EUR", "JPY").
     * - Umí pracovat s BigNumber.
     * @param val Hodnota k naformátování.
     * @param displayFormat Formátovací maska.
     * @param currency Měna.
     * @param dataType Datový typ.
     * @param formatedProviderKey klíč formátovací funkce.
     */
    public static getFormattedNumberString(
        val: string | IBigNumber | number | null | undefined,
        { displayFormat, currency, dataType }: GetFormattedNumberStringParams,
        formatedProviderKey?: string,
    ): string {
        if (val == null || val === "" || Number.isNaN(+val)) {
            return "";
        }
        const cultureInfo = CultureInfo.getCultureInfo();
        const culture = cultureInfo.name;
        if (formatedProviderKey) {
            return FormatProviderRegistrator.getFormatProvider(formatedProviderKey, val, { displayFormat, currency, dataType, culture });
        } else if (cultureInfo.numberFormat.useArabicNumbers && (displayFormat || currency || dataType)) {
            return NumberUtils.getNumberToStringFunction(displayFormat, currency, dataType).format(val);
        } else {
            // Pro formátování BigNumber podle kultury se použije vlastní implementace `toLocaleString` z třídy BigNumberProxy,
            // Ve zbývajících případech se použije nativní metoda Number.toLocaleString.
            return val instanceof BigNumberProxy ? val.toLocaleString(culture) : (+val).toLocaleString(culture);
        }
    }

    /**
     * Zjisteni poctu vyskytu retezce v nejakem nadrazenem retezci -> je to daleko rychlejsi nez RegExpy
     * @param str retezec, ve kterem se vyhledava
     * @param subString retezec, ktery se hleda
     * @returns pocet vyskytu
     */
    private static _getNumOfOccurrences(str: string, subString: string): number {
        str += "";
        subString += "";
        if (subString.length <= 0)
            return str.length + 1;
        let n = 0;
        let pos = 0;
        const step = subString.length;

        for (;;) {
            pos = str.indexOf(subString, pos);
            if (pos >= 0) {
                n++;
                pos += step;
            } else
                break;
        }
        return n;
    }

    public static getNumberToStringFunction(format?: string, currency?: string, dataType?: OWDataType | string): INumberFormatObject {
        const opts = NumberUtils.getStandardNumberFormatOptions(format, currency, dataType);
        let obj: INumberFormatObject;

        if (CultureInfo.hasI18N) {
            const cn = CultureInfo.getCultureInfo().name;

            if (format && NumberUtils._NUM_TO_STR_FUNCS[cn] && NumberUtils._NUM_TO_STR_FUNCS[cn][format])
                return NumberUtils._NUM_TO_STR_FUNCS[cn][format];

            const formatFn = (new Intl.NumberFormat(cn, opts)).format;

            obj = {
                format: function(value: any) {
                    if (typeof value === "number") {
                        return formatFn(value);
                    } else if (typeof value === "object") // BigNumber
                        return NumberUtils.numberToString(value.toString(), opts);
                    else if (typeof value === "string") { // number as string
                        return NumberUtils.numberToString(value, opts);
                    }
                    return "";
                },
            };

            if (!NumberUtils._NUM_TO_STR_FUNCS[cn])
                NumberUtils._NUM_TO_STR_FUNCS[cn] = {};

            if (format) {
                NumberUtils._NUM_TO_STR_FUNCS[cn][format] = obj;
            }
            return obj;
        } else {
            if (format && NumberUtils._NUM_TO_STR_FUNCS[format])
                return NumberUtils._NUM_TO_STR_FUNCS[format];

            obj = {
                format: (value: number) => {
                    return NumberUtils.numberToString(value.toString(), opts); // number i BigNumber
                },
            };

            if (format) {
                NumberUtils._NUM_TO_STR_FUNCS[format] = obj;
            }
            return obj;
        }
    }

    /**
     * Odstraní formátování a vrátí string s číslem v neutrální kultuře
     * @param value Hodnota, ze které má být odstraněno formátování
     * @param groupSeparator Oddělovač tisíců
     * @param decimalSeparator Oddělovač desetinných míst
     */
    public static removeFormatting(value: string, groupSeparator: string, decimalSeparator: string): string {
        const groupSeparatorRegex = NumberUtils.WHITESPACE_GROUP_SEPARATOR_REGEX.test(groupSeparator) ? NumberUtils.WHITESPACE_REGEX_GLOBAL : new RegExp(groupSeparator, "g");
        return value.replace(groupSeparatorRegex, "").replace(decimalSeparator, ".").trim();
    }

    /**
     * Zjistí minimální a maximální hodnotu pro číselný datový typ nebo null, pokud nebylo nalezeno
     * @param dataType Název číselného datového typu
     */
    public static getTypeMinMax(dataType: OWDataType | string): { min: number; max: number } | null {
        switch (dataType) {
            case OWDataType.byte:
                return { min: NumberUtils.BYTE_MIN, max: NumberUtils.BYTE_MAX };
            case OWDataType.short:
                return { min: NumberUtils.SHORT_MIN, max: NumberUtils.SHORT_MAX };
            case OWDataType.integer:
                return { min: NumberUtils.INT_MIN, max: NumberUtils.INT_MAX };
            case OWDataType.int64:
                return { min: NumberUtils.INT64_MIN, max: NumberUtils.INT64_MAX };
            case OWDataType.single:
                return { min: NumberUtils.SINGLE_MIN, max: NumberUtils.SINGLE_MAX };
            case OWDataType.decimal:
            case OWDataType.strNum:
                return { min: NumberUtils.DECIMAL_MIN, max: NumberUtils.DECIMAL_MAX };
            case OWDataType.double:
            case OWDataType.bcd:
            case OWDataType.money:
                return { min: NumberUtils.DOUBLE_MIN, max: NumberUtils.DOUBLE_MAX };
            default:
                logger.error(`Unknown data type: ${dataType}`);
                return null;
        }
    }

    /**
     * Zjistí, zda je daný datový typ celé číslo
     * @param dataType Název číselného datového typu
     */
    public static isWholeNumberType(dataType: OWDataType | string): boolean {
        return NumberUtils._WHOLE_NUMBER_TYPES.has(dataType);
    }

    /**
    * Zjistí, zda je daný datový typ číslo
    * @param dataType Název číselného datového typu
    */
    public static isNumberType(dataType: OWDataType | string): boolean {
        return NumberUtils.NUMBER_TYPES.has(dataType);
    }

    /**
     * Hodnoty null a undefined změní na "auto", k číselným hodnotám přidá "px"
     */
    public static transformLengthToCss(length: number): string {
        return length != null ? length + "px" : "auto";
    }
}

export interface INumberFormatObject {
    format: (num: any) => string;
}

export interface INumberFormatOptions extends Intl.NumberFormatOptions {
    groupSizes?: number[];
}

GlobalContext.registerType(NumberUtils);
